import { NotificationGroup, NotificationId, PeerState } from '../../../../pages/call/types';
import { AccessLevel } from '../../../../types';
import { i18n } from '../../../helpers/i18n';
import { countMetrika } from '../../../helpers/metrika';
import { getAccessLevelChangeNotification } from './helpers';
import { NotificationType } from './types';

const TIMEOUTS = {
    THREE: 3000,
    FIVE: 5000,
    SEVEN: 7000,
    TEN: 10000,
};

export const DEFAULT_TIMEOUT = TIMEOUTS.FIVE;

const ORB_ICON_WARNING = {
    name: 'warning',
    variant: 'solid',
    size: 'sm',
    color: 'var(--orb-text-primary)',
};

export const NOTIFICATIONS_MAP = {
    [NotificationId.CREATE_BROADCAST_FAILED]: () => ({
        id: 'create_broadcast_failed',
        text: i18n('statuses', 'broadcast-create-error'),
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.START_BROADCAST_FAILED]: () => ({
        id: 'start_broadcast_failed',
        text: i18n('statuses', 'broadcast-start-error'),
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.STOP_BROADCAST_FAILED]: () => ({
        id: 'stop_broadcast_failed',
        text: i18n('statuses', 'broadcast-stop-error'),
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.END_BROADCAST_FAILED]: () => ({
        id: NotificationId.END_BROADCAST_FAILED,
        text: i18n('statuses', 'broadcast-end-error'),
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.SHARE]: () => ({
        id: NotificationId.SHARE,
        type: NotificationType.SUCCESS,
        text: i18n('statuses', 'link-copied'),
        timeout: TIMEOUTS.SEVEN,
    }),
    [NotificationId.PLEASE_UNMUTE]: () => ({
        id: NotificationId.PLEASE_UNMUTE,
        priority: 1,
        type: NotificationType.INFO,
        text: i18n('statuses', 'please-unmute'),
        iconostasisIcon: {
            name: 'microOff',
            variant: 'solid',
            size: 'md',
        },
        actions: [
            {
                text: i18n('statuses', 'turn-on'),
            },
        ],
    }),
    [NotificationId.CHAT_OPEN_ERROR]: () => ({
        id: NotificationId.CHAT_OPEN_ERROR,
        type: NotificationType.ERROR,
        priority: 1,
        text: i18n('statuses', 'chat-open-error', {
            spacebreak: '\n',
        }),
        timeout: TIMEOUTS.THREE,
        icon: 'error',
    }),
    [NotificationId.CHAT_CREATE_ERROR]: () => ({
        id: NotificationId.CHAT_CREATE_ERROR,
        type: NotificationType.ERROR,
        priority: 1,
        text: i18n('statuses', 'chat-create-error', {
            spacebreak: '\n',
        }),
        timeout: TIMEOUTS.THREE,
        icon: 'error',
    }),
    [NotificationId.CHAT_HISTORY_SAVE]: () => ({
        id: NotificationId.CHAT_HISTORY_SAVE,
        type: NotificationType.INFO,
        text: i18n('record', 'notify-save-chat-history-text'),
        actions: [
            {
                text: i18n('record', 'notify-save-chat-history-cancel-button'),
                params: { isCloseNotificationAction: true },
            },
            {
                text: i18n('record', 'notify-save-chat-history-save-button'),
            },
        ],
    }),
    [NotificationId.CONFERENCE_ACCESS_LEVEL_CHANGE]: (accessLevel: AccessLevel, isWaitingRoomEnabled: boolean) => {
        const { icon, text } = getAccessLevelChangeNotification(accessLevel, isWaitingRoomEnabled);
        return {
            id: NotificationId.CONFERENCE_ACCESS_LEVEL_CHANGE,
            type: NotificationType.INFO,
            timeout: TIMEOUTS.FIVE,
            text,
            icon,
        };
    },
    [NotificationId.BROADCAST_ACCESS_LEVEL_CHANGE]: (accessLevel: AccessLevel) => ({
        id: NotificationId.BROADCAST_ACCESS_LEVEL_CHANGE,
        type: NotificationType.INFO,
        timeout: TIMEOUTS.FIVE,
        text:
            accessLevel === AccessLevel.ORGANIZATION
                ? i18n('statuses', 'notification-broadcast-access-level-organization')
                : i18n('statuses', 'notification-broadcast-access-level-public'),
        icon: accessLevel === AccessLevel.ORGANIZATION ? 'security' : 'insecurity',
    }),
    [NotificationId.OFFLINE]: () => ({
        id: NotificationId.OFFLINE,
        group: NotificationGroup.NETWORK_STATUS,
        priority: 1,
        type: NotificationType.ERROR,
        text: i18n('statuses', 'no-internet'),
    }),
    [NotificationId.RECONNECTING]: () => ({
        id: NotificationId.RECONNECTING,
        group: NotificationGroup.NETWORK_STATUS,
        priority: 1,
        icon: 'spinner',
        text: i18n('statuses', 'restoring-connection'),
    }),
    [NotificationId.ONLINE]: () => ({
        id: NotificationId.ONLINE,
        group: NotificationGroup.NETWORK_STATUS,
        priority: 1,
        type: NotificationType.SUCCESS,
        text: i18n('statuses', 'connection-established'),
        timeout: TIMEOUTS.THREE,
    }),
    [NotificationId.SCREEN_SHARING_IS_ACTIVE]: () => ({
        id: NotificationId.SCREEN_SHARING_IS_ACTIVE,
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
        text: [
            i18n('statuses', 'screen-sharing-already-active'),
            i18n('statuses', 'screen-sharing-error-info-message'),
        ],
    }),
    [NotificationId.SCREEN_SHARING_ACCESS_ERROR]: (helpSharingAccess: string) => ({
        id: NotificationId.SCREEN_SHARING_ACCESS_ERROR,
        type: NotificationType.INFO,
        timeout: TIMEOUTS.FIVE,
        text: i18n('statuses', 'screen-sharing-cancelled-or-denied-message'),
        actions: [
            {
                text: i18n('statuses', 'how-to-enable-device'),
                url: helpSharingAccess,
            },
        ],
    }),
    [NotificationId.MODERATION_KICK]: () => ({
        id: NotificationId.MODERATION_KICK,
        type: NotificationType.INFO,
        timeout: TIMEOUTS.FIVE,
        text: i18n('statuses', 'user-was-kicked'),
    }),
    [NotificationId.RECORDING_ERROR]: (text: string) => ({
        id: NotificationId.RECORDING_ERROR,
        type: NotificationType.ERROR,
        text,
        withCloseButton: true,
    }),
    [NotificationId.RECORDING_WAITING_DOWNLOAD]: () => ({
        id: NotificationId.RECORDING_WAITING_DOWNLOAD,
        type: NotificationType.INFO,
        text: i18n('record', 'waiting-download'),
    }),
    [NotificationId.FEEDBACK]: () => ({
        id: NotificationId.FEEDBACK,
        type: NotificationType.SUCCESS,
        timeout: TIMEOUTS.FIVE,
        text: i18n('feedback', 'success-send-notification-v2'),
    }),
    [NotificationId.SUPPORT_SERVICE_APPLY]: () => ({
        id: NotificationId.SUPPORT_SERVICE_APPLY,
        type: NotificationType.SUCCESS,
        timeout: TIMEOUTS.FIVE,
        text: i18n('statuses', 'feedback-sended'),
    }),
    [NotificationId.MODERATION_ERROR]: (text: string) => ({
        id: NotificationId.MODERATION_ERROR,
        timeout: TIMEOUTS.FIVE,
        type: NotificationType.ERROR,
        text,
    }),
    [NotificationId.MODERATION_MAKE_MEMBER_ERROR]: (displayName?: string) => ({
        id: NotificationId.MODERATION_MAKE_MEMBER_ERROR,
        timeout: TIMEOUTS.FIVE,
        type: NotificationType.ERROR,
        text: i18n('moderation', 'notification-make-member-error', {
            name: displayName ?? '',
        }),
    }),
    [NotificationId.MODERATION_MAKE_ADMIN_ERROR]: (displayName?: string) => ({
        id: NotificationId.MODERATION_MAKE_ADMIN_ERROR,
        timeout: TIMEOUTS.FIVE,
        type: NotificationType.ERROR,
        text: i18n('moderation', 'notification-make-admin-error', {
            name: displayName ?? '',
        }),
    }),
    [NotificationId.MODERATION_IAM_NEW_MODERATOR]: (displayName: Maybe<string>, userUid: string) => ({
        id: NotificationId.MODERATION_IAM_NEW_MODERATOR,
        timeout: TIMEOUTS.FIVE,
        text: i18n('moderation', 'notification-iam-new-co-host', {
            name: displayName || i18n('moderation', 'moderator'),
        }),
        meta: { userUID: userUid },
    }),
    [NotificationId.MODERATION_SOMEBODY_NEW_MODERATOR]: (displayName: string, userUid: Maybe<string>) => ({
        id: NotificationId.MODERATION_SOMEBODY_NEW_MODERATOR,
        timeout: TIMEOUTS.FIVE,
        text: i18n('moderation', 'notification-new-co-host', {
            name: displayName,
        }),
        meta: { userUID: userUid },
    }),
    [NotificationId.MODERATION_IAM_NOT_MODERATOR]: () => ({
        id: NotificationId.MODERATION_IAM_NOT_MODERATOR,
        timeout: TIMEOUTS.FIVE,
        iconostasisIcon: ORB_ICON_WARNING,
        text: i18n('moderation', 'notification-iam-not-co-host-v2'),
    }),
    [NotificationId.MODERATION_NO_LONGER_MODERATOR]: (displayName: string, userUid: Maybe<string>) => ({
        id: NotificationId.MODERATION_NO_LONGER_MODERATOR,
        timeout: TIMEOUTS.FIVE,
        text: i18n('moderation', 'notification-no-longer-co-host-v2', {
            name: displayName,
        }),
        meta: { userUID: userUid },
    }),
    [NotificationId.MODERATION_MUTE_AUDIO]: (isOwner: Maybe<boolean>) => ({
        id: NotificationId.MODERATION_MUTE_AUDIO,
        timeout: TIMEOUTS.FIVE,
        text: isOwner
            ? i18n('moderation', 'notification-my-audio-was-muted-by-host')
            : i18n('moderation', 'notification-my-audio-was-muted-by-co-host'),
        iconostasisIcon: {
            name: 'microOff',
            variant: 'solid',
            size: 'md',
        },
    }),
    [NotificationId.MODERATION_MUTE_VIDEO]: (isOwner: Maybe<boolean>) => ({
        id: NotificationId.MODERATION_MUTE_VIDEO,
        timeout: TIMEOUTS.FIVE,
        text: isOwner
            ? i18n('moderation', 'notification-my-video-was-muted-by-host')
            : i18n('moderation', 'notification-my-video-was-muted-by-co-host'),
        iconostasisIcon: {
            name: 'cameraOff',
            variant: 'solid',
            size: 'md',
        },
    }),
    [NotificationId.MODERATION_MUTE_SCREEN_SHARING]: (isOwner: Maybe<boolean>, userUid: Maybe<string>) => ({
        id: NotificationId.MODERATION_MUTE_SCREEN_SHARING,
        timeout: TIMEOUTS.FIVE,
        text: isOwner
            ? i18n('moderation', 'notification-my-screensharing-was-muted-by-host')
            : i18n('moderation', 'notification-my-screensharing-was-muted-by-co-host'),
        meta: { userUID: userUid },
    }),
    [NotificationId.MODERATION_MUTE_RECORDING_SUCCESS]: (withoutRemoval?: boolean, displayName?: string) => ({
        id: NotificationId.MODERATION_MUTE_RECORDING_ERROR,
        timeout: TIMEOUTS.FIVE,
        text: i18n(
            'moderation',
            withoutRemoval
                ? 'notification-action-success-mute-recording-without-removal'
                : 'notification-action-success-mute-recording',
            { name: displayName ?? '' }
        ),
    }),
    [NotificationId.MODERATION_MUTE_RECORDING_ERROR]: (displayName?: string) => ({
        id: NotificationId.MODERATION_MUTE_RECORDING_ERROR,
        timeout: TIMEOUTS.FIVE,
        text: i18n('moderation', 'notification-action-error-mute-recording', { name: displayName ?? '' }),
        iconostasisIcon: ORB_ICON_WARNING,
    }),
    [NotificationId.MODERATION_NO_PERMISSION_TO_MUTE_RECORDINGS]: () => ({
        id: NotificationId.MODERATION_NO_PERMISSION_TO_MUTE_RECORDINGS,
        timeout: TIMEOUTS.FIVE,
        text: i18n('moderation', 'notification-action-error-mute-recordings-no-permission'),
        iconostasisIcon: ORB_ICON_WARNING,
    }),
    [NotificationId.MODERATION_MUTE_ALL_RECORDING_SUCCESS]: (withoutRemoval?: boolean) => ({
        id: NotificationId.MODERATION_MUTE_ALL_RECORDING_SUCCESS,
        timeout: TIMEOUTS.FIVE,
        text: i18n(
            'moderation',
            withoutRemoval
                ? 'notification-action-success-mute-all-recordings-without-removal'
                : 'notification-action-success-mute-all-recordings'
        ),
    }),
    [NotificationId.MODERATION_MUTE_ALL_RECORDINGS_ERROR]: () => ({
        id: NotificationId.MODERATION_MUTE_ALL_RECORDINGS_ERROR,
        timeout: TIMEOUTS.FIVE,
        text: i18n('moderation', 'notification-action-error-mute-all-recordings'),
        iconostasisIcon: ORB_ICON_WARNING,
    }),
    [NotificationId.MODERATION_KICK_BAN]: (withBan: boolean, displayName: Maybe<string>) => ({
        id: NotificationId.MODERATION_KICK_BAN,
        timeout: TIMEOUTS.FIVE,
        text: i18n('moderation', withBan ? 'notification-kick-with-ban' : 'notification-kick', {
            name: displayName || '',
        }),
    }),
    [NotificationId.SET_REACTION_ERROR]: () => ({
        id: NotificationId.SET_REACTION_ERROR,
        text: i18n('moderation', 'notification-action-error'),
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.PLAN_CALL_SUCCESS]: (isBroadcast: boolean, url: string) => ({
        id: NotificationId.PLAN_CALL_SUCCESS,
        text: isBroadcast ? i18n('plan-call', 'success-planned-broadcast') : i18n('plan-call', 'success-planned-call'),
        type: NotificationType.SUCCESS,
        iconostasisIcon: {
            name: 'calendar',
            variant: 'outline',
            size: 'sm',
        },
        timeout: TIMEOUTS.TEN,
        actions: [
            {
                text: i18n('plan-call', 'go-to-event'),
                url,
            },
        ],
    }),
    [NotificationId.PLAN_CALL_ERROR]: (isBroadcast: boolean) => ({
        id: NotificationId.PLAN_CALL_ERROR,
        text: isBroadcast ? i18n('plan-call', 'error-planned-broadcast') : i18n('plan-call', 'error-planned-call'),
        type: NotificationType.ERROR,
        icon: 'error',
        timeout: TIMEOUTS.THREE,
    }),
    [NotificationId.JOINING]: () => ({
        id: NotificationId.JOINING,
        text: i18n('statuses', 'joining'),
        type: NotificationType.INFO,
        icon: 'spinner',
        meta: {
            bottomGroup: true,
        },
    }),
    [NotificationId.SEND_FEEDBACK_ERROR]: () => ({
        id: NotificationId.SEND_FEEDBACK_ERROR,
        text: i18n('feedback', 'send-feedback-error'),
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.PERMISSIONS_CLOSE_ROOM]: () => ({
        id: NotificationId.PERMISSIONS_CLOSE_ROOM,
        type: NotificationType.INFO,
        timeout: TIMEOUTS.FIVE,
        text: i18n('statuses', 'room-was-closed'),
    }),
    [NotificationId.NO_PERMISSION_TO_CLOSE_ROOM]: () => ({
        id: NotificationId.NO_PERMISSION_TO_CLOSE_ROOM,
        timeout: TIMEOUTS.FIVE,
        text: i18n('statuses', 'no-permission-to-close-room'),
        iconostasisIcon: ORB_ICON_WARNING,
    }),
    [NotificationId.PERMISSIONS_ERROR]: (text?: string) => ({
        id: NotificationId.PERMISSIONS_ERROR,
        timeout: TIMEOUTS.FIVE,
        type: NotificationType.ERROR,
        text: text || i18n('moderation', 'notification-action-error-permissions'),
    }),
    [NotificationId.WAITING_ROOM_NEW_PEER]: (peer: PeerState) => ({
        id: NotificationId.WAITING_ROOM_NEW_PEER,
        text: i18n('statuses', 'waiting-room-new-peer', { displayName: peer.displayName || '' }),
        type: NotificationType.INFO,
        withCloseButton: true,
        meta: { peerId: peer.id },
        actions: [
            {
                text: i18n('common', 'admit'),
                params: { id: peer.id, uid: peer.uid },
                buttonView: 'brand',
            },
        ],
    }),
    [NotificationId.WAITING_ROOM_NEW_PEERS]: (withViewButton: boolean) => ({
        id: NotificationId.WAITING_ROOM_NEW_PEERS,
        text: i18n('statuses', 'waiting-room-new-peers'),
        type: NotificationType.INFO,
        withCloseButton: true,
        icon: 'people',
        actions: [
            ...(withViewButton
                ? [
                      {
                          text: i18n('common', 'view'),
                          params: { type: 'view' },
                      },
                  ]
                : []),
            {
                text: i18n('common', 'admit'),
                params: { type: 'admit-all' },
                buttonView: 'brand',
            },
        ],
    }),
    [NotificationId.WAITING_ROOM_ADMIT_ERROR]: () => ({
        id: NotificationId.WAITING_ROOM_ADMIT_ERROR,
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
        text: i18n('statuses', 'waiting-room-admit-error'),
    }),
    [NotificationId.WAITING_ROOM_ADMIT_ALL_ERROR]: () => ({
        id: NotificationId.WAITING_ROOM_ADMIT_ALL_ERROR,
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
        text: i18n('statuses', 'waiting-room-admit-all-error'),
    }),
    [NotificationId.WAITING_ROOM_CONFERENCE_LIMIT]: () => ({
        id: NotificationId.WAITING_ROOM_CONFERENCE_LIMIT,
        type: NotificationType.ERROR,
        text:
            i18n('participants-sidebar', 'conference-limit-error-title') +
            '. ' +
            i18n('participants-sidebar', 'conference-limit-error-text'),
        actions: [
            {
                text: i18n('common', 'view'),
                params: { type: 'view' },
            },
        ],
    }),
    [NotificationId.WAITING_ROOM_OVERFLOW]: (withAdmitButton: boolean) => ({
        id: NotificationId.WAITING_ROOM_OVERFLOW,
        type: NotificationType.INFO,
        text: i18n('statuses', 'waiting-room-new-peers'),
        icon: 'people',
        withCloseButton: true,
        actions: [
            {
                text: i18n('common', 'view'),
                params: { type: 'view' },
            },
            ...(withAdmitButton
                ? [
                      {
                          text: i18n('common', 'admit'),
                          params: { type: 'admit-all' },
                          buttonView: 'brand',
                      },
                  ]
                : []),
        ],
    }),
    [NotificationId.NETWORK_QUALITY_BAD]: (params?: { infinite: boolean }) => {
        countMetrika('messages', 'msg_connection_unstable', params?.infinite ? 'permanent' : 'common');
        return {
            id: NotificationId.NETWORK_QUALITY_BAD,
            group: NotificationGroup.NETWORK_QUALITY,
            type: NotificationType.INFO,
            timeout: params?.infinite ? undefined : DEFAULT_TIMEOUT,
            text: i18n('statuses', 'network-quality-bad'),
            icon: 'network',
            iconFixedSize: true,
        };
    },
    [NotificationId.NETWORK_QUALITY_HIDE_VIDEO]: (isMobile: boolean) => {
        countMetrika('messages', 'msg_call_to_turn_off_incoming_video');
        return {
            id: NotificationId.NETWORK_QUALITY_HIDE_VIDEO,
            group: NotificationGroup.NETWORK_QUALITY,
            type: NotificationType.INFO,
            timeout: DEFAULT_TIMEOUT,
            text: i18n('statuses', 'network-quality-hide-video'),
            icon: !isMobile ? 'network' : undefined,
            iconFixedSize: true,
            actions: [
                {
                    text: i18n('statuses', 'open-settings'),
                    buttonView: 'brand',
                    params: {
                        metrika: ['messages', 'msg_call_to_turn_off_incoming_video_click'],
                    },
                },
            ],
        };
    },
    [NotificationId.NETWORK_QUALITY_SHOW_VIDEO]: (isMobile: boolean) => {
        countMetrika('messsages', 'msg_call_to_turn_on_incoming_video');
        return {
            id: NotificationId.NETWORK_QUALITY_SHOW_VIDEO,
            group: NotificationGroup.NETWORK_QUALITY,
            type: isMobile ? NotificationType.INFO : NotificationType.SUCCESS,
            timeout: DEFAULT_TIMEOUT,
            text: i18n('statuses', 'network-quality-show-video'),
            iconFixedSize: !isMobile,
            actions: [
                {
                    text: i18n('statuses', 'open-settings'),
                    buttonView: 'brand',
                    params: {
                        metrika: ['messages', 'msg_call_to_turn_on_incoming_video_click'],
                    },
                },
            ],
        };
    },
    [NotificationId.CLOUD_RECORDING_START]: () => ({
        id: NotificationId.CLOUD_RECORDING_START,
        type: NotificationType.INFO,
        text: i18n('statuses', 'notification-cloud-recording-in-progress'),
        icon: 'pulseRecordingDot',
        timeout: TIMEOUTS.TEN,
    }),
    [NotificationId.CLOUD_RECORDING_START_FAILED]: (isMobile: boolean) => ({
        id: NotificationId.CLOUD_RECORDING_START_FAILED,
        type: NotificationType.INFO,
        text: isMobile
            ? i18n('statuses', 'notification-cloud-recording-start-failed-mobile')
            : i18n('statuses', 'notification-cloud-recording-start-failed'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.CLOUD_RECORDING_STOP]: () => ({
        id: NotificationId.CLOUD_RECORDING_STOP,
        type: NotificationType.INFO,
        text: i18n('statuses', 'notification-cloud-recording-was-stopped'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.CLOUD_RECORDING_STOP_FAILED]: (isMobile: boolean) => ({
        id: NotificationId.CLOUD_RECORDING_STOP_FAILED,
        type: NotificationType.INFO,
        text: isMobile
            ? i18n('statuses', 'notification-cloud-recording-stop-failed-mobile')
            : i18n('statuses', 'notification-cloud-recording-stop-failed'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.SUMMARIZATION_START]: (
        isSummarizationMailForParticipants: boolean,
        isCalendarSummarizationFeatureEnabled: boolean
    ) => ({
        id: NotificationId.SUMMARIZATION_START,
        type: NotificationType.INFO,
        text: i18n(
            'statuses',
            isCalendarSummarizationFeatureEnabled
                ? 'notification-summarization-in-progress-v2'
                : isSummarizationMailForParticipants
                  ? 'summarization-in-progress-participants-will-receive'
                  : 'summarization-in-progress-admins-will-receive'
        ),
        icon: 'summarizationInProgress',
        timeout: TIMEOUTS.TEN,
    }),
    [NotificationId.SUMMARIZATION_START_FAILED]: () => ({
        id: NotificationId.SUMMARIZATION_START_FAILED,
        type: NotificationType.INFO,
        text: i18n('statuses', 'notification-summarization-start-failed-v2'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.SUMMARIZATION_STOP]: () => ({
        id: NotificationId.SUMMARIZATION_STOP,
        type: NotificationType.INFO,
        text: i18n('statuses', 'notification-summarization-was-stopped'),
        icon: 'summarization',
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.SUMMARIZATION_STOP_FAILED]: () => ({
        id: NotificationId.SUMMARIZATION_STOP_FAILED,
        type: NotificationType.INFO,
        text: i18n('statuses', 'notification-summarization-stop-failed-v2'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.SUMMARIZATION_NO_PERMISSION_TO_STOP]: () => ({
        id: NotificationId.SUMMARIZATION_NO_PERMISSION_TO_STOP,
        iconostasisIcon: ORB_ICON_WARNING,
        text: i18n('statuses', 'notification-summarization-no-permission-to-stop'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.SUMMARIZATION_MAIL_RECIPIENT]: (isSummarizationMailForParticipants: boolean) => ({
        id: NotificationId.SUMMARIZATION_MAIL_RECIPIENT,
        type: NotificationType.INFO,
        text: i18n(
            'statuses',
            isSummarizationMailForParticipants ? 'participants-will-receive-summary' : 'admins-will-receive-summary'
        ),
        timeout: TIMEOUTS.TEN,
    }),
    [NotificationId.SUMMARIZATION_MAIL_RECIPIENT_UPDATE_ERROR]: (isSummarizationMailForParticipants: boolean) => ({
        id: NotificationId.SUMMARIZATION_MAIL_RECIPIENT_UPDATE_ERROR,
        type: NotificationType.INFO,
        text: i18n(
            'statuses',
            isSummarizationMailForParticipants
                ? 'participants-will-receive-summary-update-error'
                : 'admins-will-receive-summary-update-error'
        ),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.SUMMARIZATION_NO_PERMISSION_TO_START]: () => ({
        id: NotificationId.SUMMARIZATION_NO_PERMISSION_TO_START,
        type: NotificationType.INFO,
        text: i18n('statuses', 'summarization-no-permission-to-start'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.REPORT_A_PROBLEM_SUCCESS]: () => ({
        id: NotificationId.REPORT_A_PROBLEM_SUCCESS,
        text: i18n('feedback', 'success-send-notification'),
        type: NotificationType.SUCCESS,
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.REPORT_A_PROBLEM_FAILED]: () => ({
        id: NotificationId.REPORT_A_PROBLEM_FAILED,
        text: i18n('feedback', 'send-feedback-error'),
        type: NotificationType.ERROR,
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.PERMISSIONS_EXTENDED_APPLY_SUCCESS]: () => ({
        id: NotificationId.PERMISSIONS_EXTENDED_APPLY_SUCCESS,
        text: i18n('statuses', 'permissions-extended-apply-success'),
        type: NotificationType.INFO,
        timeout: TIMEOUTS.FIVE,
        iconostasisIcon: {
            name: 'success',
            color: 'var(--orb-text-brand)',
            variant: 'solid',
            size: 'sm',
        },
    }),
    [NotificationId.PERMISSIONS_EXTENDED_APPLY_ERROR]: () => ({
        id: NotificationId.PERMISSIONS_EXTENDED_APPLY_ERROR,
        text: i18n('statuses', 'permissions-extended-apply-error'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.PERMISSIONS_EXTENDED_MATRIX_EDIT_REVOKED]: () => ({
        id: NotificationId.PERMISSIONS_EXTENDED_MATRIX_EDIT_REVOKED,
        text: i18n('statuses', 'permissions-extended-matrix-edit-revoked'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.NO_PERMISSION_TO_USE_MICROPHONE]: () => ({
        id: NotificationId.NO_PERMISSION_TO_USE_MICROPHONE,
        text: i18n('statuses', 'organizer-revoked-micro-permission'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.NO_PERMISSION_TO_SHARE_DESKTOP]: () => ({
        id: NotificationId.NO_PERMISSION_TO_SHARE_DESKTOP,
        text: i18n('statuses', 'organizer-revoked-desktop-permission'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.NO_PERMISSION_TO_USE_CAMERA]: () => ({
        id: NotificationId.NO_PERMISSION_TO_USE_CAMERA,
        text: i18n('statuses', 'organizer-revoked-camera-permission'),
    }),
    [NotificationId.INVITATION_COPIED]: () => ({
        id: NotificationId.INVITATION_COPIED,
        text: i18n('call', 'invitation-copied'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.LINK_COPIED]: () => ({
        id: NotificationId.LINK_COPIED,
        text: i18n('call', 'link-copied'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.TELE_MESSENGER_LINK_COPIED]: () => ({
        id: NotificationId.TELE_MESSENGER_LINK_COPIED,
        text: i18n('call', 'telemessenger-link-copied'),
        withCloseButton: true,
        timeout: TIMEOUTS.FIVE,
        actions: [],
    }),
    [NotificationId.LINK_COPIED_CONF_HISTORY]: () => ({
        id: NotificationId.LINK_COPIED_CONF_HISTORY,
        text: i18n('call', 'link-copied'),
        timeout: TIMEOUTS.FIVE,
        iconostasisIcon: {
            name: 'success',
            variant: 'outline',
            size: 'sm',
            color: 'var(--orb-surface-feedback-success)',
        },
    }),
    [NotificationId.PHONE_COPIED]: () => ({
        id: NotificationId.PHONE_COPIED,
        text: i18n('call', 'phone-number-copied'),
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.CONF_HISTORY_FETCHING_FAILED]: () => ({
        id: NotificationId.CONF_HISTORY_FETCHING_FAILED,
        text: i18n('conferences-history', 'batch-fetching-failed'),
        withCloseButton: true,
        iconostasisIcon: {
            name: 'warning',
            variant: 'outline',
            size: 'sm',
            color: 'var(--orb-surface-feedback-danger)',
        },
        actions: [
            {
                text: i18n('conferences-history', 'refresh'),
                buttonView: 'brand-transparent',
            },
        ],
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.EMAIL_COPIED]: () => ({
        id: NotificationId.EMAIL_COPIED,
        text: i18n('conferences-history', 'email-copied'),
        timeout: TIMEOUTS.FIVE,
        iconostasisIcon: {
            name: 'success',
            variant: 'outline',
            size: 'sm',
            color: 'var(--orb-surface-feedback-success)',
        },
    }),
    [NotificationId.REPORT_ERROR]: () => ({
        id: NotificationId.REPORT_ERROR,
        text: i18n('conferences-history', 'report-error-common'),
        withCloseButton: true,
        iconostasisIcon: {
            name: 'warning',
            variant: 'outline',
            size: 'sm',
            color: 'var(--orb-surface-feedback-danger)',
        },
        actions: [
            {
                text: i18n('conferences-history', 'contact-support'),
                buttonView: 'brand-transparent',
            },
        ],
        timeout: TIMEOUTS.FIVE,
    }),
    [NotificationId.CONNECTION_LOST]: () => ({
        id: NotificationId.CONNECTION_LOST,
        text: i18n('statuses', 'connection-lost'),
        timeout: TIMEOUTS.FIVE,
    }),
};

export const MEETING_ROOM_ALLOWED_NOTIFICATIONS = [
    NotificationId.PLEASE_UNMUTE,
    NotificationId.CHAT_OPEN_ERROR,
    NotificationId.CHAT_CREATE_ERROR,
    NotificationId.OFFLINE,
    NotificationId.RECONNECTING,
    NotificationId.ONLINE,
    NotificationId.MODERATION_MUTE_AUDIO,
    NotificationId.MODERATION_MUTE_VIDEO,
    NotificationId.NETWORK_QUALITY_BAD,
    NotificationId.CLOUD_RECORDING_START,
    NotificationId.CLOUD_RECORDING_STOP,
    NotificationId.SUMMARIZATION_START,
    NotificationId.SUMMARIZATION_STOP,
    NotificationId.SUMMARIZATION_MAIL_RECIPIENT,
    NotificationId.SET_REACTION_ERROR,
];
